export type ClubRevenueRow = {
  clubId: string;
  clubName: string;
  stripeAccountId: string;
  currency: string;
  collected: number;
  volume: number;
  membershipReceived: number;
  shopReceived: number;
  lottoReceived: number;
  refunded: number;
  platformRevenue: number;
  membershipRevenue: number;
  lottoRevenue: number;
  shopRevenue: number;
  unclassifiedRevenue: number;
  membershipCommissionPercent: number;
  lottoCommissionPercent: number;
  feeCount: number;
};

export type PlatformRevenueResponse = {
  source: "stripe_application_fees";
  currency: string;
  totals: {
    collected: number;
    volume: number;
    membershipReceived: number;
    lottoReceived: number;
    refunded: number;
    platformRevenue: number;
    membershipRevenue: number;
    lottoRevenue: number;
    shopRevenue: number;
    unclassifiedRevenue: number;
    clubs: number;
    fees: number;
  };
  daily: Array<{ date: string; total: number; membership: number; lotto: number }>;
  clubs: ClubRevenueRow[];
};

const WINDOW_SECONDS = 14 * 24 * 60 * 60;
const TOTAL_MONEY_FIELDS = [
  "collected", "volume", "membershipReceived", "lottoReceived",
  "refunded", "platformRevenue", "membershipRevenue", "lottoRevenue",
  "shopRevenue", "unclassifiedRevenue",
] as const;
const CLUB_MONEY_FIELDS = [...TOTAL_MONEY_FIELDS, "shopReceived"] as const;

// Stripe's revenue queries floor timestamps to seconds and use inclusive bounds.
// Adjacent requests must therefore end and start on consecutive UTC seconds.
export function splitRevenueRange(from: string, to: string) {
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs > toMs) {
    throw new Error("Select a valid revenue date range.");
  }

  const windows: Array<{ from: string; to: string }> = [];
  const lastSecond = Math.floor(toMs / 1000);
  for (let firstSecond = Math.floor(fromMs / 1000); firstSecond <= lastSecond;) {
    const endSecond = Math.min(firstSecond + WINDOW_SECONDS - 1, lastSecond);
    windows.push({
      from: new Date(firstSecond * 1000).toISOString(),
      to: new Date(endSecond * 1000).toISOString(),
    });
    firstSecond = endSecond + 1;
  }
  return windows;
}

const cents = (value: number) => Math.round(value * 100);
const money = (value: number) => value / 100;

export function mergeRevenueWindows(windows: PlatformRevenueResponse[]): PlatformRevenueResponse {
  if (windows.length === 0) throw new Error("No revenue windows were returned.");

  const totals = { ...windows[0].totals };
  const daily = new Map<string, { date: string; total: number; membership: number; lotto: number }>();
  const clubs = new Map<string, ClubRevenueRow>();

  for (const window of windows) {
    if (window.source !== "stripe_application_fees") throw new Error("Unexpected revenue source.");
    for (const field of TOTAL_MONEY_FIELDS) {
      if (window !== windows[0]) totals[field] = money(cents(totals[field]) + cents(window.totals[field]));
    }
    if (window !== windows[0]) totals.fees += window.totals.fees;

    for (const point of window.daily) {
      const previous = daily.get(point.date);
      daily.set(point.date, {
        date: point.date,
        total: money(cents(previous?.total ?? 0) + cents(point.total)),
        membership: money(cents(previous?.membership ?? 0) + cents(point.membership)),
        lotto: money(cents(previous?.lotto ?? 0) + cents(point.lotto)),
      });
    }

    for (const row of window.clubs) {
      const previous = clubs.get(row.stripeAccountId);
      if (!previous) {
        clubs.set(row.stripeAccountId, { ...row });
        continue;
      }
      const combined = { ...previous };
      for (const field of CLUB_MONEY_FIELDS) {
        combined[field] = money(cents(previous[field]) + cents(row[field]));
      }
      combined.feeCount += row.feeCount;
      clubs.set(row.stripeAccountId, combined);
    }
  }

  const clubRows = [...clubs.values()].sort((a, b) => b.platformRevenue - a.platformRevenue);
  totals.clubs = clubRows.length;
  return {
    source: "stripe_application_fees",
    currency: windows.find((window) => window.totals.fees > 0)?.currency ?? windows[0].currency,
    totals,
    daily: [...daily.values()].sort((a, b) => a.date.localeCompare(b.date)),
    clubs: clubRows,
  };
}
